<script>
import { describeApiError } from '@/util/api/client.js'
import { BetaApplicationResource } from '@/util/api/resources.js'
import { VOLUME_OPTIONS } from '@/util/content/marketing.js'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
    name: 'BetaForm',
    data() {
        return {
            form: {
                name: '',
                email: '',
                company: '',
                role: '',
                monthly_volume: null,
                use_case: '',
            },
            attempted: false,
            sending: false,
            error: '',
            sentTo: '',
            volumeOptions: VOLUME_OPTIONS,
        }
    },
    computed: {
        errors() {
            return {
                name: this.form.name.trim() ? '' : 'Enter your name.',
                email: EMAIL_PATTERN.test(this.form.email.trim()) ? '' : 'Enter a work email like you@company.com.',
                company: this.form.company.trim() ? '' : 'Enter your company name.',
            }
        },
        valid() {
            return Object.values(this.errors).every((message) => !message)
        },
    },
    methods: {
        stateFor(field) {
            if (!this.attempted) return null
            return this.errors[field] ? false : null
        },
        async submit() {
            this.attempted = true
            this.error = ''
            if (!this.valid) return

            this.sending = true
            try {
                const email = this.form.email.trim().toLowerCase()
                await BetaApplicationResource.submit({ ...this.form, email })
                this.sentTo = email
            } catch (error) {
                this.error = describeApiError(error)
            } finally {
                this.sending = false
            }
        },
    },
}
</script>

<template>
    <div class="ps-beta-form">
        <div v-if="sentTo" class="ps-beta-form__done" role="status">
            <b-icon icon="check-circle-fill" font-scale="2" class="ps-tone-real mb-3" />
            <h3 class="ps-title">Request received</h3>
            <p class="text-muted mb-0">We’ll reply to {{ sentTo }} about access.</p>
        </div>

        <b-form v-else novalidate @submit.prevent="submit">
            <b-form-row>
                <b-col sm="6">
                    <b-form-group label="Name" label-for="beta-name" :invalid-feedback="errors.name" :state="stateFor('name')">
                        <b-form-input id="beta-name" v-model="form.name" autocomplete="name" :state="stateFor('name')" />
                    </b-form-group>
                </b-col>
                <b-col sm="6">
                    <b-form-group label="Work email" label-for="beta-email" :invalid-feedback="errors.email" :state="stateFor('email')">
                        <b-form-input id="beta-email" v-model="form.email" type="email" autocomplete="email" inputmode="email" autocapitalize="off" :state="stateFor('email')" />
                    </b-form-group>
                </b-col>
            </b-form-row>

            <b-form-row>
                <b-col sm="6">
                    <b-form-group label="Company" label-for="beta-company" :invalid-feedback="errors.company" :state="stateFor('company')">
                        <b-form-input id="beta-company" v-model="form.company" autocomplete="organization" :state="stateFor('company')" />
                    </b-form-group>
                </b-col>
                <b-col sm="6">
                    <b-form-group label="Role" label-for="beta-role" description="Optional.">
                        <b-form-input id="beta-role" v-model="form.role" autocomplete="organization-title" />
                    </b-form-group>
                </b-col>
            </b-form-row>

            <b-form-group label="Images per month" label-for="beta-volume" description="An estimate is fine." class="ps-beta-form__volume">
                <b-form-select id="beta-volume" v-model="form.monthly_volume" :options="volumeOptions" />
            </b-form-group>

            <b-form-group label="How do you plan to use PolySigma?" label-for="beta-use-case" description="Optional.">
                <b-form-textarea id="beta-use-case" v-model="form.use_case" rows="4" max-rows="10" />
            </b-form-group>

            <b-alert :show="Boolean(error)" variant="danger" class="mb-3">{{ error }}</b-alert>

            <b-button type="submit" variant="primary" size="lg" :disabled="sending" class="ps-beta-form__submit">
                <b-spinner v-if="sending" small class="mr-2" label="Sending" />
                {{ sending ? 'Sending request' : 'Request beta access' }}
            </b-button>
        </b-form>
    </div>
</template>

<style scoped lang="scss">
.ps-beta-form ::v-deep label {
    font-weight: 600;
}

.ps-beta-form ::v-deep .form-text {
    color: var(--ps-haze) !important;
}

@media (min-width: 576px) {
    .ps-beta-form__volume {
        max-width: 50%;
        padding-right: 5px;
    }
}

.ps-beta-form__done {
    padding: 2.5rem 0;
}

@media (max-width: 575.98px) {
    .ps-beta-form__submit {
        width: 100%;
    }
}
</style>
